import { useEffect, type ReactNode } from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { useAuthStore } from "./store";
import { getCurrentUser } from "@/lib/api/auth";
import { startBackgroundRefresh, stopBackgroundRefresh } from "@/lib/api/client";

const PUBLIC_PATHS = ["/login", "/forgot-password", "/reset-password", "/callback"];

function getDefaultPath(role: string | undefined): string {
  return role === "admin" || role === "manager" ? "/" : "/simulators";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { token, setUser, logout, setLoading, isLoading, _hasHydrated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  // Start/stop background token refresh based on authentication state
  useEffect(() => {
    if (token) {
      startBackgroundRefresh();
    } else {
      stopBackgroundRefresh();
    }
    return () => stopBackgroundRefresh();
  }, [token]);

  useEffect(() => {
    if (!_hasHydrated) {
      return;
    }

    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        if (!PUBLIC_PATHS.includes(pathname)) {
          navigate({ to: "/login" });
        }
        return;
      }

      try {
        const user = await getCurrentUser();
        setUser(user);
        if (PUBLIC_PATHS.includes(pathname)) {
          navigate({ to: getDefaultPath(user.role) });
        }
      } catch {
        logout();
        if (!PUBLIC_PATHS.includes(pathname)) {
          navigate({ to: "/login" });
        }
      }
    };

    checkAuth();
  }, [token, pathname, navigate, setUser, logout, setLoading, _hasHydrated]);

  // Show loading state while hydrating or loading user
  if (!_hasHydrated || (isLoading && !PUBLIC_PATHS.includes(pathname))) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
